import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import { FC, memo, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  CourseDeliveryCountsResponse,
  CourseDeliveryDay,
  CourseDeliveryRow,
  CourseDeliverySection,
} from 'src/entities/Report.entity';

import style from './print.module.css';

// 1 ページあたりの最大行数。フォント 10.5pt に拡大したため 70 → 45 に変更。
// 最終値はブラウザ実測（A4 印刷プレビュー）で確認し必要に応じて調整すること。
export const ROWS_PER_PAGE = 45;

type Props = {
  data: CourseDeliveryCountsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  issueDate: string;
  issueTime: string;
};

type RawPage = {
  day: CourseDeliveryDay;
  section: CourseDeliverySection;
  rows: CourseDeliveryRow[];
};

export type PageInfo = RawPage & { pageInSection: number; totalInSection: number };

/**
 * 日境界 → 荷主境界 → ROWS_PER_PAGE 行ごとにページ分割する。
 * 行 0 件のセクションは表示しない。
 */
export function buildPages(days: CourseDeliveryDay[]): RawPage[] {
  const pages: RawPage[] = [];
  days.forEach((day) => {
    day.sections.forEach((section) => {
      if (section.rows.length === 0) return;
      const total = Math.ceil(section.rows.length / ROWS_PER_PAGE);
      for (let i = 0; i < total; i += 1) {
        pages.push({ day, section, rows: section.rows.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE) });
      }
    });
  });
  return pages;
}

// 和式区切り（全角空白 U+3000）は eslint no-irregular-whitespace を避けるため Unicode escape を使う
const IDEOGRAPHIC_SPACE = '\u3000';

/** 'YYYY-MM-DD' → 'YYYY年MM月DD日（全角空白）配送' */
export function formatJapaneseDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${y}年${m}月${d}日${IDEOGRAPHIC_SPACE}配送`;
}

/** (pageInSection, totalInSection) を分割確定後に計算する。 */
function withPageNumbers(pages: RawPage[]): PageInfo[] {
  const totalByKey = new Map<string, number>();
  pages.forEach((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    totalByKey.set(key, (totalByKey.get(key) ?? 0) + 1);
  });
  const indexByKey = new Map<string, number>();
  return pages.map((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    const idx = (indexByKey.get(key) ?? 0) + 1;
    indexByKey.set(key, idx);
    return { ...p, pageInSection: idx, totalInSection: totalByKey.get(key) ?? 1 };
  });
}

const CourseDeliveryCountReportPresenter: FC<Props> = memo(({ data, isLoading, isError, issueDate, issueTime }) => {
  const rawPages = useMemo(() => buildPages(data?.days ?? []), [data]);
  const pages = useMemo(() => withPageNumbers(rawPages), [rawPages]);
  const totalAll = Math.max(pages.length, 1);

  return (
    <>
      <Helmet>
        <title>コース別配送件数リスト</title>
      </Helmet>
      <div className={style.shell} role="region" aria-label="コース別配送件数リスト プレビュー">
        <div className={style.toolbar}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => window.close()}
          >
            戻る
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            コース別配送件数リスト
          </Typography>
          <Box className={style.toolbarRight}>
            <Typography variant="caption" color="text.secondary">全 {totalAll} ページ</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              印刷
            </Button>
          </Box>
        </div>

        <div className={style.body}>
          {isLoading && <div className={style.empty}>読み込み中...</div>}
          {isError && <div className={style.empty}>データの取得に失敗しました。</div>}
          {!isLoading && !isError && pages.length === 0 && (
            <div className={style.empty}>該当するデータはありません。</div>
          )}
          {pages.map((page) => (
            <div
              className={style.page}
              style={{ page: 'A4CourseDeliveryCount' }}
              key={`${page.day.delivery_date}-${page.section.shipper_name}-${page.pageInSection}`}
            >
              <div className={style.header}>
                <div className={style.title}>コース別配送件数リスト</div>
                <div className={style.sub}>
                  {page.section.shipper_name}{IDEOGRAPHIC_SPACE}{formatJapaneseDate(page.day.delivery_date)}
                </div>
                <div className={style.meta}>
                  発行日付: {issueDate}<br />
                  発行時刻: {issueTime}<br />
                  ページ: {page.pageInSection} / {page.totalInSection}
                </div>
              </div>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>コース</th>
                    {page.section.show_rotation && <th style={{ width: '8%' }}>回転数</th>}
                    <th style={{ width: '32%' }}>運送会社名</th>
                    <th>ドライバー名</th>
                    <th style={{ width: '10%' }}>配送件数</th>
                    <th style={{ width: '10%' }}>伝票数</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row, rowIdx) => (
                    <tr key={`${row.course_number ?? 'nil'}-${row.driver_name}-${rowIdx}`}>
                      <td className={style.center}>{row.course_number ?? ''}</td>
                      {page.section.show_rotation && (
                        <td className={style.center}>
                          {row.rotation_count >= 2 ? row.rotation_count : ''}
                        </td>
                      )}
                      <td>{row.carrier_company}</td>
                      <td>{row.driver_name}</td>
                      <td className={style.num}>{row.delivery_count}</td>
                      <td className={style.num}>{row.invoice_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default CourseDeliveryCountReportPresenter;
